import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { AlertCircle } from "lucide-react";
import { cloneElement, forwardRef, isValidElement, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "./Button";

export interface PopconfirmProps {
	title: React.ReactNode;
	description?: React.ReactNode;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	onConfirm?: () => void | Promise<void>;
	onCancel?: () => void;
	okText?: string;
	cancelText?: string;
	okType?: "primary" | "danger";
	okButtonProps?: React.ComponentProps<typeof Button>;
	cancelButtonProps?: React.ComponentProps<typeof Button>;
	icon?: React.ReactNode;
	placement?:
		| "top"
		| "topLeft"
		| "topRight"
		| "bottom"
		| "bottomLeft"
		| "bottomRight"
		| "left"
		| "right";
	disabled?: boolean;
	children: React.ReactElement;
}

const Popconfirm = forwardRef<HTMLDivElement, PopconfirmProps>(
	(
		{
			title,
			description,
			open: _controlledOpen,
			onOpenChange,
			onConfirm,
			onCancel,
			okText = "确定",
			cancelText = "取消",
			okType = "primary",
			okButtonProps,
			cancelButtonProps,
			icon,
			placement = "top",
			disabled = false,
			children,
		},
		_ref,
	) => {
		const [loading, setLoading] = useState(false);

		const getAnchor = () => {
			switch (placement) {
				case "top":
					return "top";
				case "topLeft":
					return "top start";
				case "topRight":
					return "top end";
				case "bottom":
					return "bottom";
				case "bottomLeft":
					return "bottom start";
				case "bottomRight":
					return "bottom end";
				case "left":
					return "left";
				case "right":
					return "right";
				default:
					return "top";
			}
		};

		const handleConfirm = async (close: () => void) => {
			try {
				setLoading(true);
				await onConfirm?.();
				close();
				onOpenChange?.(false);
			} finally {
				setLoading(false);
			}
		};

		const handleCancel = (close: () => void) => {
			onCancel?.();
			close();
			onOpenChange?.(false);
		};

		// Clone children to pass through properly
		const trigger = isValidElement(children)
			? cloneElement(children, (disabled ? { disabled: true } : {}) as any)
			: children;

		return (
			<Popover className="relative inline-block">
				{({ close }) => {
					return (
						<>
							<PopoverButton as="div" className="inline-block cursor-pointer" disabled={disabled}>
								{trigger}
							</PopoverButton>

							<PopoverPanel
								transition
								anchor={{
									to: getAnchor() as
										| "top"
										| "bottom"
										| "left"
										| "right"
										| "top start"
										| "top end"
										| "bottom start"
										| "bottom end",
									gap: 8,
								}}
								className={cn(
									"z-[1050] bg-white dark:bg-zinc-800 rounded-lg shadow-lg border border-zinc-200 dark:border-zinc-700 p-4 min-w-[200px] max-w-[300px]",
									"transition duration-150 ease-out",
									"data-[closed]:opacity-0 data-[closed]:scale-95",
								)}
							>
								<div className="flex gap-3">
									<div className="flex-shrink-0 mt-0.5">
										{icon || <AlertCircle className="h-4 w-4 text-amber-500" />}
									</div>
									<div className="flex-1">
										<div className="font-medium text-zinc-900 dark:text-zinc-100">{title}</div>
										{description && (
											<div className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
												{description}
											</div>
										)}
										<div className="mt-3 flex justify-end gap-2">
											<Button
												size="small"
												onClick={() => handleCancel(close)}
												{...cancelButtonProps}
											>
												{cancelText}
											</Button>
											<Button
												size="small"
												variant={okType === "danger" ? "danger" : "primary"}
												loading={loading}
												onClick={() => handleConfirm(close)}
												{...okButtonProps}
											>
												{okText}
											</Button>
										</div>
									</div>
								</div>
							</PopoverPanel>
						</>
					);
				}}
			</Popover>
		);
	},
);

Popconfirm.displayName = "Popconfirm";

export { Popconfirm };
